/**
 * ReactionPicker — the quick row of reactions plus, at the far right, a
 * greyed-out face with a small "+" badge (the Apple Messages pattern the owner
 * asked for, 2026-09-09) that swaps the row for the full searchable emoji
 * picker, inline in the same popover (no nested portal: Safari and Radix both
 * misbehave with one). Shared by post and comment reactions in PostCard; DMs
 * reuse MoreReactionsButton in MessageActionMenu.
 *
 * `options` are { key, emoji } pairs — the six legacy keys ('like' → 👍 …)
 * that every client has always sent. Anything picked from the full picker is
 * sent as the emoji itself, which the backend stores as the key. The user's
 * own custom reactions are appended to the quick row so they can be removed
 * with one tap.
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { LazyEmojiPicker } from '@/components/shared/EmojiPickerButton';

const SIZES = {
  md: { box: 'min-w-[44px] min-h-[44px] p-2 rounded-lg', face: 'w-7 h-7', badge: 'w-3.5 h-3.5 -top-1 -right-1', plus: 'w-2.5 h-2.5' },
  sm: { box: 'min-w-[36px] min-h-[36px] p-1.5 rounded-lg', face: 'w-5 h-5', badge: 'w-3 h-3 -top-0.5 -right-0.5', plus: 'w-2 h-2' },
  dm: { box: 'w-10 h-10 sm:w-8 sm:h-8 rounded-full', face: 'w-6 h-6 sm:w-5 sm:h-5', badge: 'w-3 h-3 -top-0.5 -right-0.5', plus: 'w-2 h-2' },
};

/**
 * Greyed-out smiley with a "+" badge on its top-right corner. Sits at the far
 * right of a reaction row and opens the full emoji picker.
 */
export function MoreReactionsButton({ onClick, size = 'md', className = '', testId = 'reaction-more' }) {
  const s = SIZES[size] || SIZES.md;
  return (
    <button
      type="button"
      onClick={onClick}
      onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={`relative flex items-center justify-center ${s.box} hover:bg-slate-100 dark:hover:bg-white/10 active:bg-slate-200 transition-colors group ${className}`}
      style={{ touchAction: 'manipulation' }}
      title="More reactions"
      aria-label="More reactions"
      data-testid={testId}
    >
      <span className="relative inline-flex">
        {/* Own SVG (not the lucide Smile) so the face can be filled without filling the mouth */}
        <svg viewBox="0 0 24 24" className={s.face} aria-hidden="true">
          <circle cx="12" cy="12" r="10" className="fill-slate-300 dark:fill-slate-600 group-hover:fill-slate-400 dark:group-hover:fill-slate-500 transition-colors" />
          <circle cx="9" cy="10" r="1.25" className="fill-slate-600 dark:fill-slate-200" />
          <circle cx="15" cy="10" r="1.25" className="fill-slate-600 dark:fill-slate-200" />
          <path d="M8 14.5c1 1.6 2.4 2.4 4 2.4s3-.8 4-2.4" className="stroke-slate-600 dark:stroke-slate-200" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </svg>
        <span className={`absolute ${s.badge} rounded-full bg-slate-500 dark:bg-slate-200 text-white dark:text-slate-900 flex items-center justify-center shadow-sm`}>
          <Plus className={s.plus} strokeWidth={3.5} />
        </span>
      </span>
    </button>
  );
}

export default function ReactionPicker({ options, selectedKeys = [], onSelect, size = 'md', testIdPrefix = 'reaction' }) {
  const [showAll, setShowAll] = useState(false);
  const compact = size === 'sm';
  const btn = compact ? 'text-lg p-1.5 min-w-[36px] min-h-[36px]' : 'text-2xl p-2 min-w-[44px] min-h-[44px]';

  if (showAll) {
    // The full picker can emit one of the quick-row glyphs (👍, ❤️ …): send its
    // legacy key so it toggles the same reaction instead of adding a twin.
    // autoFocusSearch: the "more" button that had focus is gone; keep keyboard
    // users inside the picker instead of dropping them onto <body>.
    // Skin tones and variation selectors do not make a new reaction: 👍🏽 is `like`
    // (the server and the phone fold the same way, 2026-09-09).
    const norm = (e) => String(e || '').replace(/\p{Emoji_Modifier}|\uFE0F/gu, '');
    const keyOf = Object.fromEntries(options.map((o) => [norm(o.emoji), o.key]));
    return <LazyEmojiPicker autoFocusSearch onEmojiSelect={(emoji) => onSelect(keyOf[norm(emoji)] || emoji)} />;
  }

  const extra = selectedKeys
    .filter((key) => !options.some((o) => o.key === key))
    .map((key) => ({ key, emoji: key }));

  // Touch: fire once on touchend and swallow the synthesised click — the same
  // pattern the old inline buttons used, which is reliable on iOS Safari.
  const pick = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(key);
  };

  return (
    <div className={`flex items-center ${compact ? 'gap-0.5' : 'gap-1'}`}>
      {[...options, ...extra].map(({ key, emoji }) => (
        <button
          key={key}
          type="button"
          onClick={() => onSelect(key)}
          onTouchEnd={pick(key)}
          className={`${btn} hover:scale-125 active:scale-100 transition-transform rounded-lg ${selectedKeys.includes(key) ? 'bg-blue-100 dark:bg-blue-900/50' : 'hover:bg-slate-100 dark:hover:bg-white/10 active:bg-slate-200'}`}
          style={{ touchAction: 'manipulation' }}
          title={key}
          data-testid={`${testIdPrefix}-${key}`}
        >
          {emoji}
        </button>
      ))}
      <MoreReactionsButton size={size} onClick={() => setShowAll(true)} className={compact ? 'ml-0.5' : 'ml-1'} testId={`${testIdPrefix}-more`} />
    </div>
  );
}
